<template>
  <div class="multi-select">
    <SearchableList 
      :list-items="availableOptions"
      :selected-list-items="value"
      :display-property="displayProperty"
      :value-property="valueProperty"
      :placeholder="searchPlaceholder"
      class="multi-select__list"
      @onClickOption="onOptionSelect"
    />
    
    <div class="multi-select__actions">
      <a
        class="btn btn-function m-t-30"
        :class="{'invisible': !showSelectAll}"
        @click="onSelectAllOptions"
      >
        <i class="fa fa-angle-double-right p-0" />
      </a>

      <i class="fa fa-exchange m-t-20" />

      <a
        class="btn btn-function m-t-20"
        :class="{'invisible': !showSelectAll}"
        @click="onUnselectAllOptions"
      >
        <i class="fa fa-angle-double-left p-0" />
      </a>
    </div>

    <SearchableList 
      :list-items="selectedItems"
      :no-options-text="'Select'"
      :display-property="displayProperty"
      :value-property="valueProperty"
      :placeholder="selectedPlaceholder"
      class="multi-select__selected multi-select__list"
      @onClickOption="onOptionRemove"
    />
  </div>
</template>

<script>
import { remove, isEqual } from 'lodash';

import messages from './locales';
import sharedMessages from '@/core/locales';
import SearchableList from '../SearchableList/SearchableList';


export default {
  name: 'MultiSelect',
  components: {
    SearchableList
  },
  model: {
    prop: 'value'
  },
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
      required: true
    },
    listItems: {
      type: Array,
      default() {
        return [];
      },
    },
    searchPlaceholder: {
      type: String,
      default() {
        return this.$i18n.t('SEARCH');
      }
    },
    selectedPlaceholder: {
      type: String,
      default() {
        return this.$i18n.t('SEARCH');
      }
    },
    displayProperty: {
      type: String,
      default: ''
    },
    valueProperty: {
      type: String,
      default: ''
    },
    noOptionsText: {
      type: String,
      default: ''
    },
    showSelectAll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedItems: getSelectedItemsFromValue(this.value, this.valueProperty, this.listItems)
    };
  },
  watch: {
    value(newValue) {
      this.selectedItems = getSelectedItemsFromValue(newValue, this.valueProperty, this.listItems);
    },
    listItems(newValue) {
      this.selectedItems = getSelectedItemsFromValue(this.value, this.valueProperty, newValue);
    }
  },
  computed: {
    availableOptions() {
      if (!this.value || !this.value.length) {
        return [...this.listItems];
      }

      return this.listItems.filter((option) => {
        if (this.valueProperty) {
          return this.value.indexOf(option[this.valueProperty]) < 0;
        }

        return !this.value.find((value) => {
          return isEqual(value, option);
        });
      });
    }
  },
  mounted() {
    // We do this so the ValidationProvider can validate our custom component's v-model
    // the default events when ValidationProvider does validation are `blur` and `input`
    this.$emit('blur');
  },
  methods: {
    onOptionSelect(option) {
      this.selectedItems.push(option);
      let items = [...this.value, getValueFromOption(this.valueProperty, option)];
      this.$emit('input', items);
      this.$emit('change', items);
    },
    onOptionRemove(option) {
      var items = [...this.value];
      var selectedItems = this.selectedItems;

      remove(items, function (item) {
        if (this.valueProperty) {
          return item && option && (item === option[this.valueProperty]);
        }

        return item === option;
      }.bind(this));

      remove(selectedItems, function (item) {
        if (this.valueProperty) {
          return item && option && (item[this.valueProperty] === option[this.valueProperty]);
        }

        return item === option;
      }.bind(this));

      // Copy the array because Vue doesn't react on the array modification by lodash
      // https://vuejs.org/v2/guide/list.html#Array-Change-Detection
      this.selectedItems = [...selectedItems];

      this.$emit('input', items);
      this.$emit('change', items);
    },
    onSelectAllOptions() {
      this.selectedItems = [...this.listItems];

      let selectedValues = [...getValuesFromOptions(this.valueProperty, this.listItems)];
      this.$emit('input', selectedValues);
      this.$emit('change', selectedValues);
    },
    onUnselectAllOptions() {
      this.selectedItems = [];

      this.$emit('input', []);
      this.$emit('change', []);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};

function getSelectedItemsFromValue(values, valueProperty, availableOptions) {
  if (valueProperty) {
    let selectedItems = [];

    for (const value of values) {
      let item = availableOptions.find(function findOptions(option) {
        return option[valueProperty] == value;
      });
      selectedItems.push(item);
    }

    return selectedItems;
  }

  return [...values];
}

function* getValuesFromOptions(valueProperty, options) {
  for (const option of options) {
    yield getValueFromOption(valueProperty, option);
  }
}

function getValueFromOption(valueProperty, option) {
  return valueProperty ? option[valueProperty] : option;
}
</script>

<style lang="scss" scoped>
.multi-select {
  display: inline-flex;
  height: 300px;
}

.multi-select__actions {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: 0 10px;
}

.multi-select__list {
  width: 100%;
}
</style>